import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bot,
  CheckCircle2,
  Loader2,
  Play,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { client } from "@/utils/orpc";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@super-resume-new/ui/components/card";
import { Input } from "@super-resume-new/ui/components/input";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin/ingestion")({
  head: () =>
    seo({
      title: "Ingestion Pipeline | JobCues Admin",
      description: "Trigger ATS scraping and Cerebras LLM extraction pipelines with freshness filters.",
      path: "/admin/ingestion",
      noindex: true,
    }),
  component: AdminIngestionPage,
});

function AdminIngestionPage() {
  const [includeTier1, setIncludeTier1] = useState(true);
  const [includeJobfound, setIncludeJobfound] = useState(true);
  const [jobfoundCountry, setJobfoundCountry] = useState("US");
  const [limit, setLimit] = useState(5);
  const [maxAgeDays, setMaxAgeDays] = useState(7);
  const [dryRun, setDryRun] = useState(false);
  const [skipExtraction, setSkipExtraction] = useState(false);
  const [runStats, setRunStats] = useState<any>(null);

  const queryClient = useQueryClient();

  const ingestMutation = useMutation({
    mutationFn: async () => {
      return client.admin.triggerIngestion({
        includeTier1,
        includeJobfound,
        jobfoundCountry: jobfoundCountry.trim() || undefined,
        limit,
        maxAgeDays,
        dryRun,
        skipExtraction,
      });
    },
    onSuccess: (data) => {
      setRunStats(data);
      toast.success(
        `Ingestion run finished in ${(data.durationMs / 1000).toFixed(1)}s: ${data.processed} processed, ${data.skippedDuplicates} skipped.`,
      );
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Ingestion run failed");
    },
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Ingestion Controller */}
        <Card className="lg:col-span-2 border-border bg-card">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Bot className="size-4 text-muted-foreground" />
              Ingestion Run Parameters
            </CardTitle>
            <CardDescription className="text-xs">
              Execute live job scrapers across direct ATS feeds and job aggregators. Strictly
              enforces a 7-day age cutoff and low-barrier accessibility prioritization.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Source toggles */}
              <div className="space-y-3 rounded-lg border border-border p-3.5 bg-muted/20">
                <p className="text-xs font-semibold text-foreground">Active Sources</p>
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeTier1}
                    onChange={(e) => setIncludeTier1(e.target.checked)}
                    className="rounded border-border"
                  />
                  <span>Tier 1 ATS (Greenhouse, Lever, Ashby)</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeJobfound}
                    onChange={(e) => setIncludeJobfound(e.target.checked)}
                    className="rounded border-border"
                  />
                  <span>Jobfound Aggregator API</span>
                </label>
              </div>

              {/* Execution mode */}
              <div className="space-y-3 rounded-lg border border-border p-3.5 bg-muted/20">
                <p className="text-xs font-semibold text-foreground">Pipeline Execution Mode</p>
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    className="rounded border-border"
                  />
                  <span>Dry Run (No database writes)</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={skipExtraction}
                    onChange={(e) => setSkipExtraction(e.target.checked)}
                    className="rounded border-border"
                  />
                  <span>Stage Only (Skip LLM Extraction)</span>
                </label>
              </div>
            </div>

            {/* Ingestion limits */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              <div>
                <label className="text-xs font-semibold text-foreground">Batch Limit</label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value, 10) || 5)}
                  className="mt-1 text-xs"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">Max jobs to LLM extract</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground">Max Age (Days)</label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={maxAgeDays}
                  onChange={(e) => setMaxAgeDays(parseInt(e.target.value, 10) || 7)}
                  className="mt-1 text-xs"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">Default 7-day freshness</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground">Target Country</label>
                <Input
                  value={jobfoundCountry}
                  onChange={(e) => setJobfoundCountry(e.target.value)}
                  placeholder="US, CA, IN, or empty"
                  className="mt-1 text-xs"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">ISO code for feeds</p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex items-center justify-between border-t border-border pt-4">
            <div className="text-xs text-muted-foreground">Powered by Cerebras Llama-3.3-70b</div>
            <Button
              onClick={() => ingestMutation.mutate()}
              disabled={ingestMutation.isPending}
              className="gap-2 font-semibold"
            >
              {ingestMutation.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Running Pipeline…
                </>
              ) : (
                <>
                  <Play className="size-4 fill-current" />
                  Execute Ingestion
                </>
              )}
            </Button>
          </CardFooter>
        </Card>

        {/* Right 1 Col: Run Stats & Pipeline Policy */}
        <div className="space-y-4">
          <Card className="border-border bg-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Pipeline Architecture</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5 text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="size-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span>
                  <strong>7-Day Max Age Filter:</strong> Discards old job postings before touching
                  LLM.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="size-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span>
                  <strong>SHA-256 Fingerprint:</strong> Prevents re-extracting unchanged postings.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="size-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span>
                  <strong>Entry-Level Prioritization:</strong> Internships &amp; junior jobs process
                  first.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="size-3.5 text-emerald-500 mt-0.5 shrink-0" />
                <span>
                  <strong>Validation Gate:</strong> Flagged extractions stay draft until verified.
                </span>
              </div>
            </CardContent>
          </Card>

          {runStats && (
            <Card className="border-emerald-500/30 bg-emerald-500/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold text-emerald-500 uppercase tracking-wider">
                  Latest Run Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fetched:</span>
                  <span className="font-semibold text-foreground tabular-nums">
                    {runStats.fetched}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Staged into DB:</span>
                  <span className="font-semibold text-foreground tabular-nums">
                    {runStats.staged}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Processed / Extracted:</span>
                  <span className="font-semibold text-emerald-500 tabular-nums">
                    {runStats.processed}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duplicates Skipped:</span>
                  <span className="font-semibold text-muted-foreground tabular-nums">
                    {runStats.skippedDuplicates}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Failed:</span>
                  <span className="font-semibold text-destructive tabular-nums">
                    {runStats.failed}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="font-semibold text-foreground tabular-nums">
                    {(runStats.durationMs / 1000).toFixed(2)}s
                  </span>
                </div>
                <div className="pt-2">
                  <Link to="/admin/staging" className="w-full">
                    <Button size="sm" variant="outline" className="w-full text-xs">
                      View Staging Queue
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
